import { memo } from 'react';
import {
  X,
  Loader2,
  Check,
  AlertCircle,
  RefreshCw,
  File,
  FileText,
  Image,
  Presentation,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatFileSize } from '@/lib/api/files';
import type { AttachedFile } from '@/types/files';

interface FileAttachmentItemProps {
  file: AttachedFile;
  onRemove: (id: string) => void;
  onRetry?: (id: string) => void;
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return Image;
  if (mimeType === 'application/pdf') return FileText;
  if (mimeType.includes('word')) return FileText;
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet'))
    return FileText;
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation'))
    return Presentation;
  if (mimeType === 'text/csv') return FileText;
  return File;
}

function FileAttachmentItem({ file, onRemove, onRetry }: FileAttachmentItemProps) {
  const IconComponent = getFileIcon(file.mimeType);

  const statusColors = {
    pending: 'bg-gray-100 border-gray-300',
    uploading: 'bg-info-50 border-info-300',
    uploaded: 'bg-success-50 border-success-300',
    failed: 'bg-danger-50 border-danger-300',
  };

  return (
    <div
      className={cn(
        'relative flex items-center gap-2 px-3 py-2 rounded-lg border',
        'transition-colors duration-200',
        statusColors[file.status]
      )}
    >
      {/* File Icon */}
      <div className="shrink-0">
        <IconComponent className="h-5 w-5 text-gray-600" />
      </div>

      {/* File Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {file.filename}
        </p>
        <p className="text-xs text-gray-500">{formatFileSize(file.sizeBytes)}</p>
      </div>

      {/* Status Indicator */}
      <div className="shrink-0 flex items-center gap-1">
        {file.status === 'uploading' && (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-info-600" />
            <span className="text-xs text-info-600">{file.progress}%</span>
          </>
        )}

        {file.status === 'uploaded' && (
          <Check className="h-4 w-4 text-success-600" />
        )}

        {file.status === 'failed' && (
          <div className="flex items-center gap-1">
            <AlertCircle className="h-4 w-4 text-danger-600" />
            {onRetry && (
              <button
                type="button"
                onClick={() => onRetry(file.id)}
                className="p-1 hover:bg-danger-100 rounded"
                title="Riprova"
              >
                <RefreshCw className="h-3 w-3 text-danger-600" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Remove Button */}
      <button
        type="button"
        onClick={() => onRemove(file.id)}
        className="shrink-0 p-1 hover:bg-gray-200 rounded-full transition-colors"
        title="Rimuovi"
      >
        <X className="h-4 w-4 text-gray-500" />
      </button>

      {/* Progress Bar (for uploading state) */}
      {file.status === 'uploading' && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-lg overflow-hidden">
          <div
            className="h-full bg-info-500 transition-all duration-300"
            style={{ width: `${file.progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

export default memo(FileAttachmentItem);
